/**
 * The reconcilers the `reconcile` job can run.
 *
 * Shared so the admin picker and the backend registry cannot drift: a name the
 * UI offers but the registry does not know is rejected at run time, and a
 * reconciler the registry gains but the UI does not list is unreachable
 * manually. `registry.test.ts` asserts the two agree.
 */
export interface ReconcilerDescriptor {
  /** Registry key. Sent as `input.reconcilers` when queuing a job. */
  name: string;
  /** Human label for the picker. */
  label: string;
  /** What one pass repairs, in a sentence. */
  description: string;
  /**
   * Mirrors `Reconciler.manualOnly`: the nightly sweep skips this one, so it
   * runs only when named explicitly.
   *
   * Needed here because a default run (nothing selected) covers only the
   * scheduled set. Without it the picker cannot tell the operator that leaving
   * everything unchecked silently omits these. `registry.test.ts` asserts the
   * two definitions agree.
   */
  manualOnly?: boolean;
}

export const RECONCILER_CATALOG: readonly ReconcilerDescriptor[] = [
  {
    name: "onboarding-lifecycle",
    label: "Onboarding lifecycle",
    description:
      "Re-derives the onboarding, payouts, and account health tracks from transaction history and persisted settings.",
  },
  {
    name: "billing-account",
    label: "Billing account",
    description:
      "Re-derives each merchant's billing projection from their profile, and reports the drift between the two.",
  },
  {
    name: "billing-expiry",
    label: "Billing expiry",
    description:
      "Writes down the expiries that already happened, for merchants whose paid period ended with no store notification to say so. Readers already withhold entitlement past the period end; this makes the stored row, its history, and the churn series agree with them.",
  },
  {
    name: "billing-mirror",
    label: "Billing mirror",
    manualOnly: true,
    description:
      "Re-applies the legacy plan and subscription columns from the billing projection, repairing merchants whose mirror write was dropped. Not run nightly — it is only correct for merchants already flipped to billing core as the sole writer.",
  },
  {
    name: "store-acknowledgement",
    label: "Store acknowledgement",
    description:
      "Retries the Google acknowledgements the notification path did not land. Google auto-refunds a purchase left unacknowledged for three days, so a missed one is revenue lost after the entitlement was already granted.",
  },
  {
    name: "store-rails",
    label: "Store rails",
    description:
      "Asserts each merchant's billing state against the Apple and Google subscriptions it is derived from, and asks the store about any purchase whose expiry passed long ago. Repairs entitlement left wrong by a notification that never arrived, which nothing else notices because there is no failure to see.",
  },
  {
    name: "notification-lifecycle",
    label: "Notification lifecycle",
    description:
      "Closes merchant notifications whose underlying state cleared somewhere other than the inbox, and expires what has aged out. Unresolved actionable items never expire at any age: an item either resolves or stays in the list, so nothing a merchant still needs to act on is silently dropped.",
  },
  {
    name: "account-health-notifications",
    label: "Account health notifications",
    manualOnly: true,
    description:
      "Raises the identity and bank notifications a merchant's current Stripe signals call for when nothing already carries them — a problem that predates this producer, or a profile change that never reached the stream. Safe to run repeatedly: one open row exists per business and type, so re-running produces no second one. Not run nightly — an unattended first run would page every production merchant with an undetected problem at once, and the count of those is unmeasured. Run by name, then promote to the nightly set once the repaired count from that run is known.",
  },
  {
    name: "stale-notifications",
    label: "Stale store notifications",
    description:
      "Ends store notifications left pending after their retries ran out. Lambda gives a stream consumer no signal that an attempt is its last, so the processor cannot mark them itself, and until it is marked the row claims to be queued when nothing will pick it up. Replaying one is still a deliberate act.",
  },
  {
    name: "unpaid-invoices",
    label: "Overdue invoice chases",
    description:
      "Notifies merchants about sent invoices that have passed their due date, up to 30 days past it. A sweep rather than a stream because nothing changes on an invoice when it becomes overdue \u2014 the passage of time is the trigger, and only a schedule can observe that. Safe to run repeatedly: one open chase exists per invoice, so re-running produces no second row.",
  },
  {
    name: "unpaid-invoice-window",
    label: "Overdue invoice chase cleanup",
    manualOnly: true,
    description:
      "Closes overdue-invoice chases raised before the 30-day chase window existed (CPAY-752). Run once to clear the historical backlog and verify it converges to zero; not scheduled nightly by default because it exists for that one cleanup rather than an ongoing repair.",
  },
  {
    name: "support-ticket-sync",
    label: "Support request HubSpot sync",
    manualOnly: true,
    description:
      "Re-sends support requests whose HubSpot sync failed. Nothing else can: the ticket stream only fires on insert, so a failed request is one the team never received and never will. Not run nightly — these failures usually share one cause, and a schedule would re-attempt every one of them each night until someone fixed it anyway.",
  },
];

export const RECONCILER_NAMES: readonly string[] = RECONCILER_CATALOG.map(
  (entry) => entry.name,
);
